import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { EmptyState, PrimaryButton, SecondaryButton, TextButton } from '@/lib';
import { Gated, useCapability } from '@/app/CapabilitiesProvider';
import { orgRoute } from '@/app/org-route';
import type { ApiClients } from '@/api/client';
import {
  useCapabilityMatrix,
  usePolicy,
  usePutPolicy,
  useResetPolicy,
} from './queries';
import { CapabilityDiff } from './CapabilityDiff';
import { UserOverrides } from './UserOverrides';

const ROLE_ORDER = ['viewer', 'maintainer', 'manager', 'admin'] as const;
const LOCKOUT_CAP = 'org.edit_policy';
const LOCKOUT_PHRASE = 'I understand';

export type S15RolesProps = { clients: ApiClients };

/**
 * S15 Roles. Read-only matrix for everyone; admins (cap
 * org.edit_policy) get an inline editor backed by PUT /v1/policies
 * and a reset button.
 *
 * The effective set displayed = override ∪ defaults — same
 * semantics as the backend resolver.
 */
export function S15Roles({ clients }: S15RolesProps) {
  const matrix = useCapabilityMatrix(clients);
  const policy = usePolicy(clients);
  const canEdit = useCapability('org.edit_policy');
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);

  if (matrix.isError) {
    return (
      <EmptyState
        variant="error"
        title="Couldn't load capabilities"
        body={String(matrix.error)}
      />
    );
  }
  if (matrix.isLoading || !matrix.data) {
    return <p className="text-fs-body text-gray-700">Loading…</p>;
  }

  const defaults = matrix.data.matrix as Record<string, string[]>;
  const override = (policy.data?.override ?? null) as Record<string, string[]> | null;
  const userOverrides = (policy.data?.user_overrides ?? {}) as Record<string, string[]>;
  const effective: Record<string, string[]> = {};
  for (const role of ROLE_ORDER) {
    effective[role] = override?.[role] ?? defaults[role] ?? [];
  }
  const allCaps = matrix.data.capabilities.map((c) => c.id).sort();

  return (
    <div className="flex flex-col gap-sp-6">
      <header className="flex items-baseline justify-between">
        <div>
          <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
            Access control
          </p>
          <h1 className="mt-sp-1 text-fs-h1 font-medium text-gray-900">Roles &amp; policy</h1>
          <p className="mt-sp-1 text-fs-meta text-gray-700">
            {override
              ? 'Showing your org override over ADR-0010 defaults.'
              : 'Showing ADR-0010 defaults. No org override active.'}
          </p>
        </div>
        <Gated capability="org.edit_policy">
          <div className="flex items-center gap-sp-2">
            {editing ? null : (
              <PrimaryButton label="Edit policy" onClick={() => setEditing(true)} />
            )}
          </div>
        </Gated>
      </header>

      {editing && canEdit ? (
        <PolicyEditor
          clients={clients}
          defaults={defaults}
          override={override}
          allCaps={allCaps}
          onClose={() => setEditing(false)}
        />
      ) : (
        <>
          <MatrixTable effective={effective} allCaps={allCaps} />
          {override ? (
            <section
              aria-label="Override diff against defaults"
              className="flex flex-col gap-sp-2"
            >
              <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
                Override · diff vs ADR-0010 defaults
              </p>
              <div className="grid gap-sp-2 sm:grid-cols-2">
                {ROLE_ORDER.map((role) => (
                  <CapabilityDiff
                    key={role}
                    role={role}
                    defaults={defaults[role] ?? []}
                    override={override[role]}
                  />
                ))}
              </div>
            </section>
          ) : null}
          {canEdit ? (
            <UserOverrides
              clients={clients}
              overrides={userOverrides}
              allCaps={allCaps}
            />
          ) : null}
          <footer className="border-t border-gray-300 pt-sp-3">
            <TextButton
              label="View policy change history →"
              onClick={() =>
                // The audit route redirects to settings?section=audit and
                // consumes no search params, so don't pass any (entity/actor
                // aren't part of any route's search schema).
                navigate({ to: orgRoute('/admin/audit') })
              }
            />
          </footer>
        </>
      )}
    </div>
  );
}

function MatrixTable({
  effective,
  allCaps,
}: {
  effective: Record<string, string[]>;
  allCaps: string[];
}) {
  return (
    <div className="overflow-x-auto rounded-r-card border border-gray-300 bg-gray-50">
      <table className="min-w-full text-fs-meta">
        <thead>
          <tr>
            <th className="px-sp-3 py-sp-2 text-left font-medium uppercase tracking-[0.14em] text-gray-700">
              Capability
            </th>
            {ROLE_ORDER.map((role) => (
              <th
                key={role}
                className="px-sp-3 py-sp-2 text-center font-medium uppercase tracking-[0.14em] text-gray-700"
              >
                {role}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {allCaps.map((cap) => (
            <tr key={cap} className="border-t border-gray-300">
              <td className="px-sp-3 py-sp-2 font-mono text-gray-900">{cap}</td>
              {ROLE_ORDER.map((role) => {
                const has = effective[role]?.includes(cap);
                return (
                  <td
                    key={role}
                    className="px-sp-3 py-sp-2 text-center"
                    aria-label={has ? `${role} has ${cap}` : `${role} lacks ${cap}`}
                  >
                    {has ? <span className="text-healthy-fg">●</span> : <span className="text-gray-700">—</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PolicyEditor({
  clients,
  defaults,
  override,
  allCaps,
  onClose,
}: {
  clients: ApiClients;
  defaults: Record<string, string[]>;
  override: Record<string, string[]> | null;
  allCaps: string[];
  onClose: () => void;
}) {
  const put = usePutPolicy(clients);
  const reset = useResetPolicy(clients);
  const initial = useMemo<Record<string, Set<string>>>(() => {
    const out: Record<string, Set<string>> = {};
    for (const role of ROLE_ORDER) {
      out[role] = new Set(override?.[role] ?? defaults[role] ?? []);
    }
    return out;
  }, [defaults, override]);
  const [draft, setDraft] = useState(initial);

  const toggle = (role: string, cap: string) => {
    setDraft((prev) => {
      const next = { ...prev };
      const set = new Set(next[role]);
      if (set.has(cap)) set.delete(cap);
      else set.add(cap);
      next[role] = set;
      return next;
    });
  };

  // Admin lock-out: if the draft removes org.edit_policy from the only
  // role that holds it by default (admin), surface a typed-confirmation
  // gate so the editor can't silently strip every operator of access.
  const adminDraftHasLockoutCap = draft.admin?.has(LOCKOUT_CAP) ?? false;
  const adminDefaultHasLockoutCap = (defaults.admin ?? []).includes(LOCKOUT_CAP);
  const wouldLockOut = adminDefaultHasLockoutCap && !adminDraftHasLockoutCap;
  const [lockoutConfirm, setLockoutConfirm] = useState('');
  const lockoutBlocks = wouldLockOut && lockoutConfirm.trim() !== LOCKOUT_PHRASE;

  return (
    <div className="flex flex-col gap-sp-3">
      <div className="overflow-x-auto rounded-r-card border border-gray-300 bg-gray-50">
        <table className="min-w-full text-fs-meta">
          <thead>
            <tr>
              <th className="px-sp-3 py-sp-2 text-left font-medium uppercase tracking-[0.14em] text-gray-700">
                Capability
              </th>
              {ROLE_ORDER.map((role) => (
                <th
                  key={role}
                  className="px-sp-3 py-sp-2 text-center font-medium uppercase tracking-[0.14em] text-gray-700"
                >
                  {role}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allCaps.map((cap) => (
              <tr key={cap} className="border-t border-gray-300">
                <td className="px-sp-3 py-sp-2 font-mono text-gray-900">{cap}</td>
                {ROLE_ORDER.map((role) => (
                  <td key={role} className="px-sp-3 py-sp-2 text-center">
                    <input
                      type="checkbox"
                      aria-label={`${role} has ${cap}`}
                      checked={draft[role].has(cap)}
                      onChange={() => toggle(role, cap)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {wouldLockOut ? (
        <div
          role="alert"
          className="flex flex-col gap-sp-2 rounded-r-default border border-warn bg-warn-bg px-sp-3 py-sp-2 text-fs-meta text-warn-fg"
        >
          <p>
            This change would lock everyone out of policy editing. Type
            {' '}<span className="font-mono">'I understand'</span>{' '}
            to confirm.
          </p>
          <input
            type="text"
            aria-label="Confirm lock-out by typing I understand"
            value={lockoutConfirm}
            onChange={(e) => setLockoutConfirm(e.target.value)}
            className="rounded-r-chip border border-warn bg-gray-50 px-sp-2 py-sp-1 font-mono text-fs-meta text-gray-900"
          />
        </div>
      ) : null}
      <div className="flex items-center gap-sp-3">
        <PrimaryButton
          label={put.isPending ? 'Saving…' : 'Save override'}
          onClick={() => {
            const override: Record<string, string[]> = {};
            for (const role of ROLE_ORDER) {
              override[role] = Array.from(draft[role]).sort();
            }
            put.mutate({ override }, { onSuccess: () => onClose() });
          }}
          disabled={put.isPending || lockoutBlocks}
        />
        <SecondaryButton label="Cancel" onClick={onClose} />
        <TextButton
          label={reset.isPending ? 'Resetting…' : 'Reset to ADR defaults'}
          onClick={() => {
            if (window.confirm('Drop your org override and restore ADR-0010 defaults?')) {
              reset.mutate(undefined, { onSuccess: () => onClose() });
            }
          }}
        />
        {put.isError ? (
          <span role="alert" className="text-fs-meta text-critical">
            {String(put.error)}
          </span>
        ) : null}
      </div>
    </div>
  );
}
